import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Zap, Mail, Users, Target, BarChart3, FileText, ArrowRight, Clock, CheckCircle2, AlertTriangle, XCircle, Loader2, MessageSquare, Send } from 'lucide-react';
import { getAnalyticsOverview, getCampaigns } from '../lib/api';

export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [allCampaigns, setAllCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [overviewRes, campaignsRes] = await Promise.all([
        getAnalyticsOverview(),
        getCampaigns(),
      ]);
      setOverview(overviewRes.data);
      setRecentCampaigns(campaignsRes.data.slice(0, 5));
      setAllCampaigns(campaignsRes.data);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const statusBadge = (status) => {
    const styles = {
      pipeline_running: 'bg-[hsl(var(--accent)/0.15)] text-[hsl(var(--accent))] border border-[hsl(var(--accent)/0.25)]',
      approved: 'bg-[hsl(var(--status-success)/0.15)] text-[hsl(var(--status-success))] border border-[hsl(var(--status-success)/0.25)]',
      sent: 'bg-[hsl(var(--status-success)/0.15)] text-[hsl(var(--status-success))] border border-[hsl(var(--status-success)/0.25)]',
      review_needed: 'bg-[hsl(var(--status-warning)/0.15)] text-[hsl(var(--status-warning))] border border-[hsl(var(--status-warning)/0.25)]',
      error: 'bg-[hsl(var(--status-danger)/0.15)] text-[hsl(var(--status-danger))] border border-[hsl(var(--status-danger)/0.25)]',
      sending: 'bg-[hsl(var(--accent)/0.15)] text-[hsl(var(--accent))] border border-[hsl(var(--accent)/0.25)]',
      draft: 'bg-secondary text-foreground',
    };
    return <Badge className={`text-xs ${styles[status] || styles.draft}`}>{status?.replace('_', ' ')}</Badge>;
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[1,2,3,4].map(i => (
            <div key={i} className="kpi-card animate-pulse">
              <div className="h-4 bg-muted rounded w-20 mb-3"></div>
              <div className="h-8 bg-muted rounded w-16"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const kpis = [
    { label: 'Campaigns', value: overview?.total_campaigns || 0, icon: Mail, color: 'var(--accent)' },
    { label: 'Contacts', value: overview?.total_contacts || 0, icon: Users, color: 'var(--chart-2)' },
    { label: 'Leads', value: overview?.total_leads || 0, icon: Target, color: 'var(--chart-3)' },
    { label: 'Emails Sent', value: overview?.metrics?.sent || 0, icon: BarChart3, color: 'var(--chart-4)' },
  ];

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Overview of your campaigns and performance</p>
        </div>
        <Link to="/campaigns/new">
          <Button data-testid="create-campaign-btn" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white">
            <Zap className="w-4 h-4 mr-2" /> New Campaign
          </Button>
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="kpi-cards">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.label} className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{kpi.label}</span>
                  <Icon className="w-4 h-4" style={{color: `hsl(${kpi.color})`}} />
                </div>
                <p className="text-3xl font-semibold tracking-tight" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  {kpi.value.toLocaleString()}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Campaign Status Breakdown */}
      {allCampaigns.length > 0 && (() => {
        const statusCounts = { approved: 0, sent: 0, review_needed: 0, pipeline_running: 0, error: 0, draft: 0 };
        allCampaigns.forEach(c => { statusCounts[c.status] = (statusCounts[c.status] || 0) + 1; });
        const total = allCampaigns.length;
        const statusItems = [
          { key: 'approved', label: 'Approved', count: statusCounts.approved, icon: CheckCircle2, color: 'hsl(var(--status-success))' },
          { key: 'sent', label: 'Sent', count: statusCounts.sent, icon: Send, color: 'hsl(var(--accent))' },
          { key: 'review_needed', label: 'Needs Review', count: statusCounts.review_needed, icon: AlertTriangle, color: 'hsl(var(--status-warning))' },
          { key: 'pipeline_running', label: 'Running', count: statusCounts.pipeline_running, icon: Loader2, color: 'hsl(var(--accent))' },
          { key: 'error', label: 'Failed', count: statusCounts.error + (statusCounts.pipeline_error || 0), icon: XCircle, color: 'hsl(var(--status-danger))' },
        ].filter(s => s.count > 0);

        return (
          <Card className="bg-card/90 border border-border mb-8" style={{boxShadow: 'var(--shadow-elev-1)'}} data-testid="campaign-status-breakdown">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Campaign Status Breakdown</p>
                <span className="text-xs text-muted-foreground">{total} total</span>
              </div>
              <div className="flex gap-1 h-2 rounded-full overflow-hidden mb-4" data-testid="status-progress-bar">
                {statusItems.map(s => (
                  <div key={s.key} style={{ width: `${(s.count / total) * 100}%`, backgroundColor: s.color, minWidth: s.count > 0 ? '8px' : '0' }} className="transition-all duration-500" title={`${s.label}: ${s.count}`} />
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {statusItems.map(s => {
                  const Icon = s.icon;
                  return (
                    <div key={s.key} className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-secondary/30 border border-border/50" data-testid={`status-count-${s.key}`}>
                      <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: s.color }} />
                      <div>
                        <p className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>{s.count}</p>
                        <p className="text-[10px] text-muted-foreground">{s.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Metrics Row */}
      {overview?.metrics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
            <CardContent className="p-5">
              <p className="text-xs text-muted-foreground mb-1">Opens</p>
              <p className="text-2xl font-semibold" style={{fontFamily: 'Space Grotesk'}}>{overview.metrics.opens}</p>
              <p className="text-xs text-muted-foreground mt-1">{overview.metrics.unique_opens} unique</p>
            </CardContent>
          </Card>
          <Card className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
            <CardContent className="p-5">
              <p className="text-xs text-muted-foreground mb-1">Clicks</p>
              <p className="text-2xl font-semibold" style={{fontFamily: 'Space Grotesk'}}>{overview.metrics.clicks}</p>
              <p className="text-xs text-muted-foreground mt-1">{overview.metrics.unique_clicks} unique</p>
            </CardContent>
          </Card>
          <Card className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
            <CardContent className="p-5">
              <p className="text-xs text-muted-foreground mb-1">Forms</p>
              <p className="text-2xl font-semibold" style={{fontFamily: 'Space Grotesk'}}>{overview.total_forms}</p>
              <p className="text-xs text-muted-foreground mt-1">Active forms</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Campaigns */}
      <Card className="bg-card/90 border border-border" style={{boxShadow: 'var(--shadow-elev-1)'}}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold">Recent Campaigns</CardTitle>
            <Link to="/campaigns">
              <Button variant="ghost" size="sm" className="text-xs text-muted-foreground">
                View all <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {recentCampaigns.length === 0 ? (
            <div className="text-center py-8">
              <Zap className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No campaigns yet. Create your first campaign!</p>
              <Link to="/campaigns/new">
                <Button className="mt-3 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" size="sm">
                  Create Campaign
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {recentCampaigns.map((campaign) => (
                <Link key={campaign.id} to={`/campaigns/${campaign.id}`}
                  className="flex items-center justify-between p-3 rounded-lg table-row-hover transition-colors duration-150 cursor-pointer"
                  data-testid={`campaign-row-${campaign.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{campaign.subject_line || campaign.topic}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground font-mono">
                        {new Date(campaign.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {statusBadge(campaign.status)}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-7 w-7 p-0 text-[hsl(var(--accent))]" 
                      onClick={(e) => { e.preventDefault(); navigate(`/campaign-builder?campaign=${campaign.id}`); }}
                      data-testid={`dashboard-edit-chat-${campaign.id}`}
                      title="Edit in Chat"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                    </Button>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
